(ns io.daskul.webapp.widgets.billing.line-editor-logic
  "Pure, React-free logic for the unified invoice line editor (#843).

   Runs under the :node-test build so it stays testable in isolation (separate
   logic from layout). The line editor is a controlled component over a `lines`
   vector; these functions build that vector from the /billing/billables API and
   project it back onto the POST /billing/invoices item shape at submit — a line
   maps 1:1 to a payload item.

   Line kinds:
   - :enrollment — auto-populated from a billable; provenance carried, beneficiary
     derived + locked by the UI (it is the enrolled student).
   - :charge     — ad-hoc manual line (blank description / qty / unit-price).
   - :credit     — negative-unit-price line (added in a later slice)."
  (:require
   [clojure.string :as str]
   [io.daskul.webapp.widgets.billing.billables-logic :as bl]))

(def ^:private provenance-keys
  "Billable provenance carried verbatim onto an enrollment line and persisted by
   create-invoice! into invoice_line_items."
  [:source-type :source-id :offering-type :offering-id
   :beneficiary-type :beneficiary-id])

(def item-keys
  "The POST /billing/invoices item shape. Projecting a line onto exactly these
   keys drops UI-only fields (:kind, :line-id, discount display state)."
  [:description :quantity :unit-price
   :discount-amount :discount-type :discount-note :discount-id
   :offering-type :offering-id :beneficiary-type :beneficiary-id
   :discountable :source-type :source-id])

(defn line-key
  "Stable local key for an enrollment line, derived from its provenance so a
   removed line re-includes as the same identity (the billable set is fixed)."
  [item]
  (str "enrollment:" (:source-id item) ":" (:offering-id item)
       ":" (:beneficiary-id item)))

(defn billable->line
  "Map one /billing/billables panel item to an editor line. Enrollment lines
   carry their provenance and are flagged :enrollment so the UI derives + locks
   the beneficiary (it comes from the enrollment's student). The student's name —
   already on the billable as its group label — rides along as :beneficiary-name
   for display (a UI-only hint, dropped at the payload boundary). :group and
   :package-id (UI grouping / stale key) are dropped."
  [item]
  (merge
   (select-keys item [:description :quantity :unit-price :discountable])
   (select-keys item provenance-keys)
   {:kind :enrollment
    :line-id (line-key item)
    :beneficiary-name (get-in item [:group :label])}))

(defn billables->lines
  "Flatten /billing/billables panels into the editor's initial line vector —
   one line per item, provenance preserved, enrollment-flagged."
  [panels]
  (into []
        (comp (mapcat :items) (map billable->line))
        panels))

(defn line-subtotal
  "Net subtotal for a line: qty × unit-price − discount (credits carry a negative
   unit-price, so their subtotal is negative)."
  [{:keys [quantity unit-price discount-amount]}]
  (- (* (or quantity 1) (or unit-price 0))
     (or discount-amount 0)))

(defn lines-total
  "Editor total over the lines vector = Σ line subtotals."
  [lines]
  (reduce + 0 (map line-subtotal lines)))

(defn credit?
  "A credit line carries a negative unit-price (Add credit); it is not a charge."
  [line]
  (= :credit (:kind line)))

(defn- line-gross [{:keys [quantity unit-price]}]
  (* (or quantity 1) (or unit-price 0)))

(defn lines-charges
  "Gross charges = Σ (qty × unit-price) across the charge/enrollment lines, before
   per-line discounts. Credits are excluded (they ride the credits bucket)."
  [lines]
  (reduce + 0 (map #(if (credit? %) 0 (line-gross %)) lines)))

(defn lines-discount-total
  "Total of per-line discount amounts across lines."
  [lines]
  (reduce + 0 (map #(or (:discount-amount %) 0) lines)))

(defn lines-credit-total
  "Total credit magnitude = Σ |credit line subtotal| (a positive number)."
  [lines]
  (reduce + 0 (map #(if (credit? %) (- (line-gross %)) 0) lines)))

(def ^:private discount-keys #{:discount-amount :discount-type :discount-note :discount-id})

(defn line->item
  "Project an editor line onto the POST /billing/invoices item shape. Non-discount
   keys are carried when present. Discount fields are folded in only when the line
   carries a positive discount amount — a cleared (0) or absent discount sends no
   discount keys, and a one-off (no :discount-id) sends no id — so the payload
   never carries nil :discount-amount (the schema's :double rejects it)."
  [line]
  (let [base (reduce (fn [m k]
                       (if (and (not (discount-keys k)) (contains? line k))
                         (assoc m k (get line k))
                         m))
                     {}
                     item-keys)
        disc (:discount-amount line)]
    (if (and (number? disc) (pos? disc))
      (cond-> (assoc base :discount-amount (double disc))
        (:discount-type line) (assoc :discount-type (:discount-type line))
        (:discount-note line) (assoc :discount-note (:discount-note line))
        (:discount-id line)   (assoc :discount-id (:discount-id line)))
      base)))

;; ---------------------------------------------------------------------------
;; Per-line discounts (criterion ④)
;;
;; Auto-match mirrors the picker's resolver (offering + beneficiary, largest
;; amount wins) but yields :discount-amount directly, so there is no
;; :discount → :discount-amount rename (#835) to carry. The matched values are
;; the baseline; the user can lower/clear/restore per line. Display state is
;; derived by comparing the line's current amount against the freshly recomputed
;; match, so it survives dropping :discount-id on a one-off.
;; ---------------------------------------------------------------------------

(defn match-line-discount
  "Best auto-match discount for a single line (offering + beneficiary), or nil.
   Manual charge/credit lines never match (no :discountable / no offering)."
  [line discounts]
  (when-let [cands (seq (filter #(bl/discount-matches-item? % line) discounts))]
    (let [amt (fn [d] (bl/compute-discount-amount
                       (:value_type d) (double (:value d))
                       (double (:unit-price line 0)) (double (:quantity line 1))))
          best (apply max-key amt cands)]
      {:discount-amount (amt best)
       :discount-type (:discount_type best)
       :discount-note (get-in best [:data :note])
       :discount-id (:id best)})))

(defn apply-auto-discounts
  "Fold each line's auto-matched discount in as the :auto baseline. Lines with no
   match are left untouched."
  [lines discounts]
  (if (empty? discounts)
    lines
    (mapv (fn [line]
            (if-let [m (match-line-discount line discounts)]
              (merge line m)
              line))
          lines)))

(defn discount-state
  "Classify a line's discount for the sub-line label + struck-original display,
   comparing its current amount against the recomputed match amount:
     :auto    — a match exists and current equals it (untouched)
     :custom  — a discount differs from the match, or is set with no match
     :removed — a match exists but current was cleared to 0
     :none    — no match and no discount"
  [{:keys [current-amount matched-amount]}]
  (let [cur (or current-amount 0)
        has-match? (and (number? matched-amount) (pos? matched-amount))]
    (cond
      (and has-match? (== cur matched-amount)) :auto
      (and has-match? (zero? cur))             :removed
      (pos? cur)                               :custom
      :else                                    :none)))

;; ---------------------------------------------------------------------------
;; Editor state = {:lines [...] :available [...]}
;;
;; :lines are on the invoice; :available holds enrollment lines the customer has
;; but that were excluded from this invoice. The billable set is fixed and known,
;; so an excluded enrollment line is never destroyed — it drops into :available
;; and can be re-included. Manual charge/credit lines have no source, so removing
;; one deletes it outright.
;; ---------------------------------------------------------------------------

(defn- by-id [line-id] (fn [l] (= (:line-id l) line-id)))

(defn exclude-line
  "Remove a line by id. Enrollment lines move to :available (re-includable);
   manual charge/credit lines are dropped entirely."
  [{:keys [lines available] :as state} line-id]
  (if-let [line (first (filter (by-id line-id) lines))]
    (let [lines' (filterv (complement (by-id line-id)) lines)]
      (if (= :enrollment (:kind line))
        {:lines lines' :available (conj (vec available) line)}
        {:lines lines' :available (vec available)}))
    state))

(defn include-line
  "Move an excluded enrollment line from :available back onto the invoice."
  [{:keys [lines available] :as state} line-id]
  (if-let [line (first (filter (by-id line-id) available))]
    {:lines (conj (vec lines) line)
     :available (filterv (complement (by-id line-id)) available)}
    state))

;; ---------------------------------------------------------------------------
;; Required-field validation (surfaced on Save)
;;
;; Enrollment lines are auto-populated from a billable and their qty/price are
;; locked, so they carry no user-required fields — adjustments ride a discount or
;; credit line. A manual charge and a credit are freely edited, so they must be
;; complete before the invoice can be saved.
;; ---------------------------------------------------------------------------

(defn- blank? [v] (or (nil? v) (and (string? v) (str/blank? v))))

(defn line-errors
  "The set of invalid required-field keys for a line (empty set = valid).
   :charge  → :description, :quantity (≥1), :unit-price.
   :credit  → :description (the reason), :quantity (≥1), :unit-price (per-unit amount).
   :enrollment → none (values come from the billable and are locked)."
  [line]
  (case (:kind line)
    :enrollment #{}
    :credit (cond-> #{}
              (blank? (:description line))                              (conj :description)
              (or (nil? (:quantity line)) (not (pos? (:quantity line)))) (conj :quantity)
              (nil? (:unit-price line))                                (conj :unit-price))
    (cond-> #{}
      (blank? (:description line))                              (conj :description)
      (or (nil? (:quantity line)) (not (pos? (:quantity line)))) (conj :quantity)
      (nil? (:unit-price line))                                (conj :unit-price))))

(defn lines-invalid
  "Map of line-id → invalid field set, for every line that has an error. Empty
   when the whole set is savable."
  [lines]
  (into {} (keep (fn [l] (let [e (line-errors l)] (when (seq e) [(:line-id l) e]))) lines)))
